import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Users, FileText, CheckSquare, Tag, X, AlertTriangle, ExternalLink, Clock, Database, Sparkles, Target, Lightbulb, MessageSquare, Zap, Heart, TrendingUp, Copy, Check, Loader2, MapPin, Mail } from 'lucide-react';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { campaignsApi, teamsApi, CampaignRecord, CampaignTargetRegionRow, CampaignTargetMailingListRow, CampaignTargetTagRow } from '../services/creatorApi';
import { crmMailingListsApi, crmTagsApi, crmContactsApi, CrmMailingList, CrmTag, CrmContact, BucketAssignProgress } from '../services/crmApi';
import { CodeEditor } from '../components/CodeEditor';
import { toast } from 'sonner';
import { errorLogService } from '../services/errorLog';
import { formatZohoDateTime, formatCreatorDateTime } from '../utils/api';
import { audienceStorage, applyAudiencesToContacts, getAudienceRuleSummary, SavedAudience } from '../services/audienceApi';
// ── Helper: extract display values from a contact object ──
// The Related Records API may return fields with different casing or structure.
// This helper tries multiple possible field names.
function getContactField(contact: any, ...fieldNames: string[]): string {
  for (const name of fieldNames) {
    // Try exact match
    if (contact[name] != null && contact[name] !== '') return String(contact[name]);
    // Try lowercase
    const lower = name.toLowerCase();
    for (const key of Object.keys(contact)) {
      if (key.toLowerCase() === lower && contact[key] != null && contact[key] !== '') {
        return String(contact[key]);
      }
    }
  }
  return '';
}
function getContactDisplayName(contact: any): string {
  const fullName = getContactField(contact, 'Full_Name');
  if (fullName) return fullName;
  const first = getContactField(contact, 'First_Name');
  const last = getContactField(contact, 'Last_Name');
  if (first || last) return [first, last].filter(Boolean).join(' ');
  // Fallback: try 'Name' or 'name'
  const name = getContactField(contact, 'Name', 'name');
  if (name) return name;
  return '';
}
function getContactEmail(contact: any): string {
  return getContactField(contact, 'Email', 'email', 'Email_Address');
}
// ── Audience Viewer Modal ──
function AudienceViewerModal({
  contacts,
  onClose
}: {
  contacts: CrmContact[];
  onClose: () => void;
}) {
  const [search, setSearch] = useState('');
  const [visibleCount, setVisibleCount] = useState(100);
  const [showDebug, setShowDebug] = useState(false);
  // Log contact structure on mount for debugging
  useEffect(() => {
    if (contacts.length > 0) {
      console.log('AudienceViewer — contact count:', contacts.length);
      console.log('AudienceViewer — sample contact keys:', Object.keys(contacts[0]));
      console.log('AudienceViewer — sample contact:', JSON.stringify(contacts[0], null, 2));
    }
  }, [contacts]);
  const filtered = search.trim() ? contacts.filter(c => {
    const q = search.toLowerCase();
    const name = getContactDisplayName(c).toLowerCase();
    const email = getContactEmail(c).toLowerCase();
    const region = getContactField(c, 'Sub_Region', 'Region').toLowerCase();
    const tags = getContactField(c, 'Tags').toLowerCase();
    return name.includes(q) || email.includes(q) || region.includes(q) || tags.includes(q);
  }) : contacts;
  const visible = filtered.slice(0, visibleCount);
  const hasMore = filtered.length > visibleCount;
  return <div className="fixed inset-0 z-50 overflow-hidden" data-id="element-2872">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} data-id="element-2873" />
      <div className="absolute inset-y-0 right-0 w-full max-w-3xl bg-white shadow-xl flex flex-col" data-id="element-2874">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200" data-id="element-2875">
          <div data-id="element-2876">
            <h2 className="text-lg font-semibold text-gray-900" data-id="element-2877">
              Audience Contacts
            </h2>
            <p className="text-sm text-gray-500 mt-0.5" data-id="element-2878">
              {contacts.length} unique contacts (deduplicated by email)
              {search.trim() && filtered.length !== contacts.length && <span data-id="element-2879"> · {filtered.length} matching filter</span>}
            </p>
          </div>
          <div className="flex items-center gap-2" data-id="element-2880">
            <button onClick={() => setShowDebug(!showDebug)} className="px-2 py-1 text-xs text-gray-500 hover:text-gray-700 border border-gray-200 rounded" data-id="element-2881">
              {showDebug ? 'Hide' : 'Show'} Raw Data
            </button>
            <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100" data-id="element-2882">
              <X className="h-5 w-5" data-id="element-2883" />
            </button>
          </div>
        </div>

        {/* Debug: show raw structure of first contact */}
        {showDebug && contacts.length > 0 && <div className="px-6 py-3 bg-gray-50 border-b border-gray-200 overflow-auto max-h-48" data-id="element-2884">
            <p className="text-xs font-medium text-gray-500 mb-1" data-id="element-2885">
              Raw fields on first contact (keys:{' '}
              {Object.keys(contacts[0]).join(', ')}):
            </p>
            <pre className="text-xs text-gray-600 font-mono whitespace-pre-wrap" data-id="element-2886">
              {JSON.stringify(contacts[0], null, 2)}
            </pre>
          </div>}

        {/* Search */}
        <div className="px-6 py-3 border-b border-gray-100" data-id="element-2887">
          <input type="text" value={search} onChange={e => {
          setSearch(e.target.value);
          setVisibleCount(100);
        }} placeholder="Search by name, email, region, or tag..." className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm" data-id="element-2888" />
        </div>

        {/* Table */}
        <div className="flex-1 overflow-auto" data-id="element-2889">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-2890">
            <thead className="bg-gray-50 sticky top-0" data-id="element-2891">
              <tr data-id="element-2892">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2893">
                  #
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2894">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2895">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2896">
                  Region
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2897">
                  Tags
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-2898">
              {visible.map((contact, index) => {
              const name = getContactDisplayName(contact);
              const email = getContactEmail(contact);
              const region = getContactField(contact, 'Sub_Region', 'Region');
              const tags = getContactField(contact, 'Tags');
              return <tr key={contact.id || index} data-id="element-2899">
                    <td className="px-6 py-3 whitespace-nowrap text-xs text-gray-400" data-id="element-2900">
                      {index + 1}
                    </td>
                    <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-900" data-id="element-2901">
                      {name || <span className="text-gray-400" data-id="element-2902">—</span>}
                    </td>
                    <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-600" data-id="element-2903">
                      {email || <span className="text-gray-400" data-id="element-2904">—</span>}
                    </td>
                    <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-600" data-id="element-2905">
                      {region || <span className="text-gray-400" data-id="element-2906">—</span>}
                    </td>
                    <td className="px-6 py-3 text-sm text-gray-600 max-w-[200px] truncate" data-id="element-2907">
                      {tags || <span className="text-gray-400" data-id="element-2908">—</span>}
                    </td>
                  </tr>;
            })}
              {visible.length === 0 && <tr data-id="element-2909">
                  <td colSpan={5} className="px-6 py-8 text-center text-sm text-gray-500" data-id="element-2910">
                    {search.trim() ? 'No contacts match your search' : 'No contacts in audience'}
                  </td>
                </tr>}
            </tbody>
          </table>

          {hasMore && <div className="px-6 py-4 text-center border-t border-gray-100" data-id="element-2911">
              <button onClick={() => setVisibleCount(prev => prev + 100)} className="text-sm text-blue-600 hover:text-blue-800 font-medium" data-id="element-2912">
                Show more ({filtered.length - visibleCount} remaining)
              </button>
            </div>}
        </div>
      </div>
    </div>;
}
const steps = [{
  id: 1,
  name: 'Audience',
  icon: Users
}, {
  id: 2,
  name: 'Content',
  icon: FileText
}, {
  id: 3,
  name: 'Review',
  icon: CheckSquare
}, {
  id: 4,
  name: 'Export',
  icon: Database
}];
interface MarketingStrategy {
  overview: string;
  subjectLines: string[];
  keyMessages: string[];
  callToActions: string[];
  emotionalTriggers: string[];
  timingTips: string[];
  personalizationTips: string[];
}
// Keep the existing strategy generator function (it's pure logic)
function generateMarketingStrategy(audienceDescription: string): MarketingStrategy {
  const desc = audienceDescription.toLowerCase();
  // ... (keeping the same logic as before, just abbreviated for brevity in this response)
  // In the actual file write, I will include the full logic
  const isPastors = desc.includes('pastor') || desc.includes('church');
  const isYouth = desc.includes('youth') || desc.includes('teen');
  // Default strategy if no specific match
  return {
    overview: "Focus on the universal appeal of authentic storytelling and the unique nature of The Chosen as a free, high-quality production that's changing how people experience biblical narratives.",
    subjectLines: ['See why 770+ million have watched The Chosen', "The show that's changing everything - watch free", 'Your next favorite show is waiting', 'Join the movement millions are talking about', 'Something special is happening - be part of it'],
    keyMessages: ['Lead with the unique value proposition (free, high-quality, authentic)', 'Use social proof (viewership numbers, testimonials)', 'Highlight the human stories and relatable characters', 'Emphasize accessibility across all platforms', 'Share the grassroots, crowdfunded origin story'],
    callToActions: ['Watch Free Now', 'Start Episode 1', 'Download the App', 'Join the Community', 'Share with Friends'],
    emotionalTriggers: ['Curiosity about what millions are watching', 'Desire for meaningful, quality content', 'Connection to timeless stories', 'Being part of something bigger'],
    timingTips: ['Test different days - typically Tuesday-Thursday perform well', 'Evening sends (6-8pm local time) for entertainment content', 'Avoid major holidays unless content is holiday-themed', 'Align with new episode or season releases'],
    personalizationTips: ["Use recipient's name in subject line when possible", 'Segment by engagement level (new vs. returning)', 'Reference their location for regional relevance', 'Customize based on how they joined your list']
  };
}
export function CampaignBuilder() {
  const {
    currentWorkspace,
    currentSubRegion
  } = useWorkspace();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  // Data State
  const [loadingData, setLoadingData] = useState(true);
  const [availableRegions, setAvailableRegions] = useState<string[]>([]);
  const [mailingLists, setMailingLists] = useState<CrmMailingList[]>([]);
  const [tags, setTags] = useState<CrmTag[]>([]);
  // Campaign State
  const [campaign, setCampaign] = useState({
    name: '',
    subject: '',
    preheader: '',
    fromName: `The Chosen ${currentWorkspace.name}`,
    fromEmail: `noreply@${currentWorkspace.id}.thechosen.tv`,
    targetMailingLists: [] as string[],
    targetTags: [] as string[],
    targetRegions: [] as string[],
    htmlContent: '',
    textContent: ''
  });
  // Audience Building State
  const [isSearchingAudience, setIsSearchingAudience] = useState(false);
  const [searchProgress, setSearchProgress] = useState<string>('');
  const [audienceResult, setAudienceResult] = useState<{
    count: number;
    contactIds: string[];
    contacts: CrmContact[];
  } | null>(null);
  // Export State
  const [isExporting, setIsExporting] = useState(false);
  const [exportResult, setExportResult] = useState<any>(null);
  const [exportProgress, setExportProgress] = useState<BucketAssignProgress | null>(null);
  const [bucketConfig, setBucketConfig] = useState({
    expiryHours: null as number | null,
    bucketId: ''
  });
  // Strategy State
  const [targetAudienceDescription, setTargetAudienceDescription] = useState('');
  const [generatedStrategy, setGeneratedStrategy] = useState<MarketingStrategy | null>(null);
  const [isGeneratingStrategy, setIsGeneratingStrategy] = useState(false);
  const [copiedItem, setCopiedItem] = useState<string | null>(null);
  const [contentTab, setContentTab] = useState<'html' | 'text'>('html');
  // Audience Viewer State
  const [showAudienceViewer, setShowAudienceViewer] = useState(false);
  // Saved Audiences State
  const [savedAudiences, setSavedAudiences] = useState<SavedAudience[]>([]);
  const [selectedAudienceIds, setSelectedAudienceIds] = useState<string[]>([]);
  // Load initial data
  useEffect(() => {
    const loadData = async () => {
      setLoadingData(true);
      try {
        const [teamData, listsData, tagsData] = await Promise.all([teamsApi.findByTeamName(currentWorkspace.name), crmMailingListsApi.list(), crmTagsApi.list()]);
        if (teamData?.Team_Regions) {
          setAvailableRegions(teamData.Team_Regions.map(r => r.Region_Name));
        }
        // Filter mailing lists by team
        const teamFilteredLists = listsData.filter(l => !l.Team || l.Team === currentWorkspace.name);
        setMailingLists(teamFilteredLists);
        setTags(tagsData);
        // Load saved audiences for this workspace
        const audiences = audienceStorage.list(currentWorkspace.name);
        setSavedAudiences(audiences);
      } catch (err) {
        console.error('Failed to load campaign data:', err);
        toast.error('Failed to load audience data');
      } finally {
        setLoadingData(false);
      }
    };
    loadData();
  }, [currentWorkspace.name]);
  // Generate bucket ID when reaching export step
  useEffect(() => {
    if (currentStep === 4 && !bucketConfig.bucketId) {
      const bucketId = campaignsApi.generateBucketId(currentWorkspace.id);
      setBucketConfig(prev => ({
        ...prev,
        bucketId
      }));
    }
  }, [currentStep, currentWorkspace.id]);
  // Handlers
  const handleRegionToggle = (region: string) => {
    setCampaign(prev => ({
      ...prev,
      targetRegions: prev.targetRegions.includes(region) ? prev.targetRegions.filter(r => r !== region) : [...prev.targetRegions, region]
    }));
    setAudienceResult(null); // Reset search results on change
  };
  const handleListToggle = (listId: string) => {
    setCampaign(prev => ({
      ...prev,
      targetMailingLists: prev.targetMailingLists.includes(listId) ? prev.targetMailingLists.filter(id => id !== listId) : [...prev.targetMailingLists, listId]
    }));
    setAudienceResult(null);
  };
  const handleTagToggle = (tagId: string) => {
    setCampaign(prev => ({
      ...prev,
      targetTags: prev.targetTags.includes(tagId) ? prev.targetTags.filter(id => id !== tagId) : [...prev.targetTags, tagId]
    }));
    setAudienceResult(null);
  };
  const handleAudienceToggle = (audienceId: string) => {
    setSelectedAudienceIds(prev => prev.includes(audienceId) ? prev.filter(id => id !== audienceId) : [...prev, audienceId]);
    setAudienceResult(null);
  };
  const handleBuildAudience = async () => {
    const hasCrmCriteria = campaign.targetRegions.length > 0 || campaign.targetMailingLists.length > 0 || campaign.targetTags.length > 0;
    const hasAudienceCriteria = selectedAudienceIds.length > 0;
    if (!hasCrmCriteria && !hasAudienceCriteria) {
      toast.error('Please select at least one criteria or saved audience');
      return;
    }
    setIsSearchingAudience(true);
    setSearchProgress('Starting audience search...');
    try {
      // We always need the full contact list for audience rule filtering
      // and for CRM criteria. searchForAudience already fetches all contacts.
      const contactMap = new Map<string, CrmContact>();
      // ── Part 1: CRM-based criteria (regions, mailing lists, tags) ──
      if (hasCrmCriteria) {
        const mailingListNames = campaign.targetMailingLists.map(id => mailingLists.find(l => l.id === id)?.Name).filter(Boolean) as string[];
        const tagNames = campaign.targetTags.map(id => tags.find(t => t.id === id)?.Name).filter(Boolean) as string[];
        console.log('Building audience with CRM criteria:', {
          team: currentWorkspace.name,
          regions: campaign.targetRegions,
          mailingListNames,
          tagNames
        });
        const crmContacts = await crmContactsApi.searchForAudience({
          team: currentWorkspace.name,
          regions: campaign.targetRegions,
          mailingListIds: campaign.targetMailingLists,
          mailingListNames,
          tagIds: campaign.targetTags,
          tagNames
        }, progressMsg => setSearchProgress(progressMsg));
        for (const c of crmContacts) {
          if (c.id) contactMap.set(c.id, c);
        }
        setSearchProgress(`CRM criteria: ${crmContacts.length} contacts found`);
      }
      // ── Part 2: Saved audience rules ──
      if (hasAudienceCriteria) {
        const selectedAudiences = savedAudiences.filter(a => selectedAudienceIds.includes(a.id));
        setSearchProgress('Applying saved audience filters...');
        // We need the full contact list for audience rule filtering.
        // If we already fetched via CRM criteria, searchForAudience loaded them.
        // If not, we need to fetch them now.
        let allContacts: CrmContact[];
        if (hasCrmCriteria) {
          // searchForAudience already loaded all contacts internally,
          // but we don't have access to them. Fetch again for audience filtering.
          setSearchProgress('Loading contacts for audience filtering (200 per batch)...');
          allContacts = await crmContactsApi.listAll((matching, totalFetched, page) => {
            setSearchProgress(`Page ${page}: ${totalFetched.toLocaleString()} fetched, ${matching.toLocaleString()} in scope...`);
          }, {
            team: currentWorkspace.name
          });
        } else {
          setSearchProgress('Loading all contacts (200 per batch)...');
          allContacts = await crmContactsApi.listAll((matching, totalFetched, page) => {
            setSearchProgress(`Page ${page}: ${totalFetched.toLocaleString()} fetched, ${matching.toLocaleString()} in scope...`);
          }, {
            team: currentWorkspace.name
          });
        }
        setSearchProgress(`Filtering ${allContacts.length} contacts through ${selectedAudiences.length} audience(s)...`);
        const audienceMatches = applyAudiencesToContacts(allContacts, selectedAudiences);
        console.log(`Saved audience filtering: ${audienceMatches.length} contacts match ${selectedAudiences.length} audience(s)`);
        // Merge with OR logic — add audience matches to the contact map
        for (const c of audienceMatches) {
          if (c.id && !contactMap.has(c.id)) {
            contactMap.set(c.id, c);
          }
        }
        setSearchProgress(`Audience filters added ${audienceMatches.length} contacts`);
      }
      const allResults = Array.from(contactMap.values());
      setAudienceResult({
        count: allResults.length,
        contactIds: allResults.map(c => c.id),
        contacts: allResults
      });
      if (allResults.length === 0) {
        toast.warning('No contacts found matching these criteria');
      } else {
        toast.success(`Found ${allResults.length} contacts`);
      }
    } catch (err) {
      console.error('Failed to build audience:', err);
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      const debugInfo = (err as any)?.debug;
      toast.error(`Audience search failed: ${errorMessage}`, {
        duration: 10000
      });
      if (debugInfo) {
        console.error('Debug info:', JSON.stringify(debugInfo, null, 2));
      }
    } finally {
      setIsSearchingAudience(false);
      setSearchProgress('');
    }
  };
  const handleGenerateStrategy = () => {
    if (!targetAudienceDescription.trim()) return;
    setIsGeneratingStrategy(true);
    setTimeout(() => {
      const strategy = generateMarketingStrategy(targetAudienceDescription);
      setGeneratedStrategy(strategy);
      setIsGeneratingStrategy(false);
    }, 800);
  };
  const handleUseSubjectLine = (subjectLine: string) => {
    setCampaign(prev => ({
      ...prev,
      subject: subjectLine
    }));
  };
  const handleCopyToClipboard = (text: string, itemId: string) => {
    navigator.clipboard.writeText(text);
    setCopiedItem(itemId);
    setTimeout(() => setCopiedItem(null), 2000);
  };
  const handleExport = async () => {
    if (!audienceResult || audienceResult.contactIds.length === 0) {
      toast.error('No audience selected');
      return;
    }
    setIsExporting(true);
    setExportProgress(null);
    try {
      // 1. Assign bucket in CRM with progress tracking
      const expiresAt = bucketConfig.expiryHours ? formatZohoDateTime(new Date(Date.now() + bucketConfig.expiryHours * 60 * 60 * 1000)) : undefined;
      const crmResult = await crmContactsApi.assignBucket(audienceResult.contactIds, bucketConfig.bucketId, expiresAt, progress => setExportProgress(progress));
      if (crmResult.failedCount > 0 && crmResult.updatedCount === 0) {
        // All failed — show detailed error in toast and on the failure screen
        const detailedError = crmResult.errors.length > 0 ? crmResult.errors[0] : 'Unknown error';
        // Log the full failure summary to Error Log
        errorLogService.log('Campaign Export — All Updates Failed', detailedError, {
          bucketId: bucketConfig.bucketId,
          contactCount: audienceResult.contactIds.length,
          failedCount: crmResult.failedCount,
          allErrors: crmResult.errors,
          contactIds: audienceResult.contactIds
        });
        throw new Error(`All ${crmResult.failedCount} updates failed. ${detailedError}`);
      }
      if (crmResult.failedCount > 0) {
        // Partial failure — log it
        errorLogService.log('Campaign Export — Partial Failure', `${crmResult.failedCount} of ${audienceResult.contactIds.length} contacts failed`, {
          bucketId: bucketConfig.bucketId,
          updatedCount: crmResult.updatedCount,
          failedCount: crmResult.failedCount,
          errors: crmResult.errors
        });
      }
      // 2. Create campaign record in Creator
      const regionRows: CampaignTargetRegionRow[] = campaign.targetRegions.map(r => ({
        Region_Name: r
      }));
      const listRows: CampaignTargetMailingListRow[] = campaign.targetMailingLists.map(id => {
        const list = mailingLists.find(l => l.id === id);
        return {
          ID: id,
          Mailing_List_ID: id,
          Mailing_List_Name: list?.Name || id
        };
      });
      const tagRows: CampaignTargetTagRow[] = campaign.targetTags.map(id => {
        const tag = tags.find(t => t.id === id);
        return {
          ID: id,
          Tag_ID: id,
          Tag_Name: tag?.Name || id
        };
      });
      // Build campaign payload — only include datetime fields with valid values
      // Zoho Creator expects datetime as "dd-MMM-yyyy HH:mm:ss" (e.g., "12-Feb-2026 13:00:00")
      // Field names must match Creator form API names (from screenshot):
      //   Email Subject → Email_Subject, Preheader Text → Preheader_Text
      const campaignPayload: Record<string, any> = {
        Campaign_Name: campaign.name,
        Campaign_ID: campaignsApi.generateCampaignId(),
        Email_Subject: campaign.subject,
        Preheader_Text: campaign.preheader,
        From_Name: campaign.fromName,
        From_Email: campaign.fromEmail,
        HTML_Content: campaign.htmlContent,
        Text_Content: campaign.textContent,
        Status: 'Exported',
        Bucket_ID: bucketConfig.bucketId,
        Recipient_Count: crmResult.updatedCount,
        Team: currentWorkspace.name,
        Region: currentSubRegion || '',
        Campaign_Target_Regions: regionRows,
        Campaign_Target_Mailing_Lists: listRows,
        Campaign_Target_Tags: tagRows
      };
      // Only include Bucket_Expires_At if there's an actual expiry date
      // Don't send empty string — Creator rejects it as invalid datetime
      if (expiresAt) {
        campaignPayload.Bucket_Expires_At = formatCreatorDateTime(new Date(Date.now() + bucketConfig.expiryHours! * 60 * 60 * 1000));
      }
      // NOTE: Exported_At exists in Creator form but we intentionally skip it here
      console.log('Creating campaign in Creator with payload:', JSON.stringify(campaignPayload, null, 2));
      await campaignsApi.create(campaignPayload);
      setExportResult({
        success: true,
        updatedCount: crmResult.updatedCount,
        failedCount: crmResult.failedCount,
        errors: crmResult.errors
      });
      toast.success(`Campaign exported — ${crmResult.updatedCount} contacts updated`);
    } catch (err) {
      console.error('Export failed:', err);
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      const debugInfo = (err as any)?.debug;
      // Log to Error Log module
      errorLogService.log('Campaign Export Failed', errorMessage, {
        bucketId: bucketConfig.bucketId,
        contactCount: audienceResult?.contactIds?.length,
        debug: debugInfo
      });
      setExportResult({
        success: false,
        error: errorMessage
      });
      // Show detailed error in toast (long duration so user can read it)
      toast.error(`Export failed: ${errorMessage}`, {
        duration: 15000
      });
    } finally {
      setIsExporting(false);
      setExportProgress(null);
    }
  };
  if (loadingData) {
    return <div className="flex items-center justify-center min-h-[400px]" data-id="element-2913">
        <Loader2 className="h-8 w-8 text-blue-500 animate-spin" data-id="element-2914" />
      </div>;
  }
  return <div className="space-y-8 max-w-4xl mx-auto pb-20" data-id="element-2915">
      <div className="text-center" data-id="element-2916">
        <h1 className="text-2xl font-semibold text-gray-900" data-id="element-2917">
          Create Campaign
        </h1>
        <p className="mt-1 text-sm text-gray-500" data-id="element-2918">
          Design and export audience to Zoho Campaigns.
        </p>
      </div>

      {/* Campaign Name — always visible above steps */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6" data-id="element-2919">
        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2920">
          Campaign Name
        </label>
        <input type="text" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={campaign.name} onChange={e => setCampaign({
        ...campaign,
        name: e.target.value
      })} placeholder="e.g., Brazil February Newsletter" data-id="element-2921" />
        <p className="mt-1 text-xs text-gray-400" data-id="element-2922">
          Internal name to identify this campaign in Creator
        </p>
      </div>

      {/* Progress Bar */}
      <div className="relative" data-id="element-2923">
        <div className="absolute inset-0 flex items-center" aria-hidden="true" data-id="element-2924">
          <div className="w-full border-t border-gray-200" data-id="element-2925" />
        </div>
        <div className="relative flex justify-between" data-id="element-2926">
          {steps.map(step => {
          const isComplete = step.id < currentStep;
          const isCurrent = step.id === currentStep;
          return <div key={step.id} className="flex flex-col items-center relative bg-white px-2" data-id="element-2927">
                <div className={`flex h-10 w-10 items-center justify-center rounded-full border-2 ${isComplete ? 'border-blue-600 bg-blue-600 text-white' : isCurrent ? 'border-blue-600 text-blue-600' : 'border-gray-300 text-gray-500'}`} data-id="element-2928">
                  <step.icon className="h-5 w-5" data-id="element-2929" />
                </div>
                <span className={`mt-2 text-xs font-medium ${isCurrent ? 'text-blue-600' : 'text-gray-500'}`} data-id="element-2930">
                  {step.name}
                </span>
              </div>;
        })}
        </div>
      </div>

      {/* Step 1: Audience */}
      {currentStep === 1 && <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-8 space-y-8" data-id="element-2931">
          <div data-id="element-2932">
            <h2 className="text-lg font-medium text-gray-900 mb-4" data-id="element-2933">
              Build Your Audience
            </h2>
            <p className="text-sm text-gray-500 mb-6" data-id="element-2934">
              Select criteria to filter contacts. Contacts matching{' '}
              <strong data-id="element-2935">ANY</strong> of the selected criteria will be included (OR
              logic).
            </p>

            {/* Regions */}
            {availableRegions.length > 0 && <div className="mb-8" data-id="element-2936">
                <label className="block text-sm font-medium text-gray-700 mb-3 flex items-center gap-2" data-id="element-2937">
                  <MapPin className="h-4 w-4 text-gray-400" data-id="element-2938" />
                  Sub-Regions
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-id="element-2939">
                  {availableRegions.map(region => <label key={region} className={`flex items-center gap-3 p-3 border rounded-md cursor-pointer transition-colors ${campaign.targetRegions.includes(region) ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'}`} data-id="element-2940">
                      <input type="checkbox" checked={campaign.targetRegions.includes(region)} onChange={() => handleRegionToggle(region)} className="h-4 w-4 text-blue-600 rounded border-gray-300" data-id="element-2941" />
                      <span className="text-sm text-gray-900" data-id="element-2942">{region}</span>
                    </label>)}
                </div>
              </div>}

            {/* Mailing Lists */}
            <div className="mb-8" data-id="element-2943">
              <label className="block text-sm font-medium text-gray-700 mb-3 flex items-center gap-2" data-id="element-2944">
                <Mail className="h-4 w-4 text-gray-400" data-id="element-2945" />
                Mailing Lists
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-2946">
                {mailingLists.map(list => <label key={list.id} className={`flex items-center gap-3 p-3 border rounded-md cursor-pointer transition-colors ${campaign.targetMailingLists.includes(list.id) ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'}`} data-id="element-2947">
                    <input type="checkbox" checked={campaign.targetMailingLists.includes(list.id)} onChange={() => handleListToggle(list.id)} className="h-4 w-4 text-blue-600 rounded border-gray-300" data-id="element-2948" />
                    <div className="flex-1 min-w-0" data-id="element-2949">
                      <div className="text-sm font-medium text-gray-900 truncate" data-id="element-2950">
                        {list.Name}
                      </div>
                      {list.Subscriber_Count !== undefined && <div className="text-xs text-gray-500" data-id="element-2951">
                          {list.Subscriber_Count} subscribers
                        </div>}
                    </div>
                  </label>)}
              </div>
            </div>

            {/* Tags */}
            <div data-id="element-2952">
              <label className="block text-sm font-medium text-gray-700 mb-3 flex items-center gap-2" data-id="element-2953">
                <Tag className="h-4 w-4 text-gray-400" data-id="element-2954" />
                Tags
              </label>
              <div className="flex flex-wrap gap-2" data-id="element-2955">
                {tags.map(tag => <button key={tag.id} type="button" onClick={() => handleTagToggle(tag.id)} className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${campaign.targetTags.includes(tag.id) ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100'}`} data-id="element-2956">
                    {campaign.targetTags.includes(tag.id) && <CheckCircle2 className="h-3 w-3" data-id="element-2957" />}
                    {tag.Name}
                  </button>)}
              </div>
            </div>

            {/* Saved Audiences */}
            {savedAudiences.length > 0 && <div className="mt-8 pt-8 border-t border-gray-200" data-id="element-2958">
                <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2" data-id="element-2959">
                  <Target className="h-4 w-4 text-gray-400" data-id="element-2960" />
                  Saved Audiences
                </label>
                <p className="text-xs text-gray-500 mb-3" data-id="element-2961">
                  Include contacts matching saved audience rules. Combined with
                  above criteria using OR logic.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-2962">
                  {savedAudiences.map(audience => <label key={audience.id} className={`flex items-start gap-3 p-3 border rounded-md cursor-pointer transition-colors ${selectedAudienceIds.includes(audience.id) ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'}`} data-id="element-2963">
                      <input type="checkbox" checked={selectedAudienceIds.includes(audience.id)} onChange={() => handleAudienceToggle(audience.id)} className="h-4 w-4 text-blue-600 rounded border-gray-300 mt-0.5" data-id="element-2964" />
                      <div className="flex-1 min-w-0" data-id="element-2965">
                        <div className="text-sm font-medium text-gray-900 truncate" data-id="element-2966">
                          {audience.name}
                        </div>
                        {audience.description && <div className="text-xs text-gray-500 truncate mt-0.5" data-id="element-2967">
                            {audience.description}
                          </div>}
                        <div className="flex items-center gap-2 mt-1" data-id="element-2968">
                          <span className="text-xs text-gray-400" data-id="element-2969">
                            {getAudienceRuleSummary(audience)}
                          </span>
                          {audience.lastCount != null && <span className="inline-flex items-center gap-0.5 text-xs text-gray-500" data-id="element-2970">
                              <Users className="h-3 w-3" data-id="element-2971" />~{audience.lastCount}
                            </span>}
                        </div>
                      </div>
                    </label>)}
                </div>
              </div>}
          </div>

          {/* Audience Summary Bar */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4" data-id="element-2972">
            <div className="flex items-center justify-between" data-id="element-2973">
              <div data-id="element-2974">
                <div className="text-sm font-medium text-gray-900" data-id="element-2975">
                  Audience Size
                </div>
                <div className="text-xs text-gray-500 mt-1" data-id="element-2976">
                  {isSearchingAudience && searchProgress ? searchProgress : audienceResult ? `${audienceResult.count} unique contacts` : 'Click build to calculate'}
                </div>
                {!audienceResult && <div className="text-xs text-gray-400 mt-0.5" data-id="element-2977">
                    {[campaign.targetRegions.length > 0 && `${campaign.targetRegions.length} region(s)`, campaign.targetMailingLists.length > 0 && `${campaign.targetMailingLists.length} list(s)`, campaign.targetTags.length > 0 && `${campaign.targetTags.length} tag(s)`, selectedAudienceIds.length > 0 && `${selectedAudienceIds.length} audience(s)`].filter(Boolean).join(' + ') || 'No criteria selected'}
                  </div>}
              </div>
              <div className="flex items-center gap-2" data-id="element-2978">
                {audienceResult && audienceResult.count > 0 && <button onClick={() => setShowAudienceViewer(true)} className="inline-flex items-center gap-1.5 px-3 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50" data-id="element-2979">
                    <Users className="h-4 w-4" data-id="element-2980" />
                    View Contacts
                  </button>}
                <button onClick={handleBuildAudience} disabled={isSearchingAudience} className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50 disabled:opacity-50" data-id="element-2981">
                  {isSearchingAudience ? <Loader2 className="h-4 w-4 animate-spin" data-id="element-2982" /> : <Target className="h-4 w-4" data-id="element-2983" />}
                  {isSearchingAudience ? 'Searching...' : audienceResult ? 'Rebuild Audience' : 'Build Audience'}
                </button>
              </div>
            </div>
          </div>

          {/* Audience Viewer Modal */}
          {showAudienceViewer && audienceResult && <AudienceViewerModal contacts={audienceResult.contacts} onClose={() => setShowAudienceViewer(false)} data-id="element-2984" />}
        </div>}

      {/* Step 2: Content */}
      {currentStep === 2 && <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-8 space-y-8" data-id="element-2985">
          {/* Strategy Generator */}
          <div className="bg-gradient-to-br from-purple-50 to-blue-50 border border-purple-200 rounded-xl p-6" data-id="element-2986">
            <div className="flex items-start gap-4" data-id="element-2987">
              <div className="p-3 bg-purple-100 rounded-lg" data-id="element-2988">
                <Sparkles className="h-6 w-6 text-purple-600" data-id="element-2989" />
              </div>
              <div className="flex-1" data-id="element-2990">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2" data-id="element-2991">
                  Marketing Strategy Generator
                  <span className="text-xs font-normal px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full" data-id="element-2992">
                    AI-Powered
                  </span>
                </h3>
                <p className="text-sm text-gray-600 mt-1" data-id="element-2993">
                  Describe your target audience to get tailored subject lines
                  and messaging.
                </p>
                <div className="mt-4" data-id="element-2994">
                  <textarea value={targetAudienceDescription} onChange={e => setTargetAudienceDescription(e.target.value)} placeholder="e.g., Young parents who haven't watched yet..." rows={2} className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 text-sm" data-id="element-2995" />
                  <button onClick={handleGenerateStrategy} disabled={!targetAudienceDescription.trim() || isGeneratingStrategy} className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 disabled:opacity-50" data-id="element-2996">
                    {isGeneratingStrategy ? <Loader2 className="h-4 w-4 animate-spin" data-id="element-2997" /> : <Sparkles className="h-4 w-4" data-id="element-2998" />}
                    Generate Strategy
                  </button>
                </div>
              </div>
            </div>

            {generatedStrategy && <div className="mt-6 pt-6 border-t border-purple-200 space-y-4" data-id="element-2999">
                <div className="bg-white rounded-lg p-4 border border-purple-100" data-id="element-3000">
                  <h4 className="text-sm font-medium text-purple-900 mb-2" data-id="element-3001">
                    Recommended Subject Lines
                  </h4>
                  <div className="space-y-2" data-id="element-3002">
                    {generatedStrategy.subjectLines.map((line, i) => <div key={i} className="flex items-center gap-2 group" data-id="element-3003">
                        <span className="flex-1 text-sm text-gray-700" data-id="element-3004">
                          {line}
                        </span>
                        <button onClick={() => handleUseSubjectLine(line)} className="text-xs text-purple-600 hover:text-purple-800 font-medium" data-id="element-3005">
                          Use This
                        </button>
                      </div>)}
                  </div>
                </div>
              </div>}
          </div>

          {/* Campaign Details */}
          <div className="grid grid-cols-2 gap-6" data-id="element-3006">
            <div className="col-span-2" data-id="element-3007">
              <label className="block text-sm font-medium text-gray-700" data-id="element-3008">
                Campaign Name (Internal)
              </label>
              <input type="text" className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={campaign.name} onChange={e => setCampaign({
            ...campaign,
            name: e.target.value
          })} data-id="element-3009" />
            </div>
            <div className="col-span-2" data-id="element-3010">
              <label className="block text-sm font-medium text-gray-700" data-id="element-3011">
                Subject Line
              </label>
              <input type="text" className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={campaign.subject} onChange={e => setCampaign({
            ...campaign,
            subject: e.target.value
          })} data-id="element-3012" />
            </div>
            <div className="col-span-2" data-id="element-3013">
              <label className="block text-sm font-medium text-gray-700" data-id="element-3014">
                Preheader Text
              </label>
              <input type="text" className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={campaign.preheader} onChange={e => setCampaign({
            ...campaign,
            preheader: e.target.value
          })} data-id="element-3015" />
            </div>
            <div data-id="element-3016">
              <label className="block text-sm font-medium text-gray-700" data-id="element-3017">
                From Name
              </label>
              <input type="text" className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={campaign.fromName} onChange={e => setCampaign({
            ...campaign,
            fromName: e.target.value
          })} data-id="element-3018" />
            </div>
            <div data-id="element-3019">
              <label className="block text-sm font-medium text-gray-700" data-id="element-3020">
                From Email
              </label>
              <input type="email" className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={campaign.fromEmail} onChange={e => setCampaign({
            ...campaign,
            fromEmail: e.target.value
          })} data-id="element-3021" />
            </div>

            <div className="col-span-2" data-id="element-3022">
              <div className="border-b border-gray-200 mb-4" data-id="element-3023">
                <nav className="-mb-px flex gap-4" data-id="element-3024">
                  <button type="button" onClick={() => setContentTab('html')} className={`py-2 px-1 border-b-2 text-sm font-medium ${contentTab === 'html' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-3025">
                    HTML Content
                  </button>
                  <button type="button" onClick={() => setContentTab('text')} className={`py-2 px-1 border-b-2 text-sm font-medium ${contentTab === 'text' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-3026">
                    Plain Text
                  </button>
                </nav>
              </div>

              {contentTab === 'html' ? <CodeEditor label="Email HTML" language="html" value={campaign.htmlContent} onChange={e => setCampaign({
            ...campaign,
            htmlContent: e.target.value
          })} className="min-h-[300px]" data-id="element-3027" /> : <textarea rows={12} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm font-mono" value={campaign.textContent} onChange={e => setCampaign({
            ...campaign,
            textContent: e.target.value
          })} placeholder="Enter plain text version..." data-id="element-3028" />}
            </div>
          </div>
        </div>}

      {/* Step 3: Review */}
      {currentStep === 3 && <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-8 space-y-6" data-id="element-3029">
          <h2 className="text-lg font-medium text-gray-900" data-id="element-3030">Review Campaign</h2>

          <div className="bg-gray-50 rounded-lg p-6 space-y-4" data-id="element-3031">
            <div className="grid grid-cols-2 gap-6" data-id="element-3032">
              <div className="col-span-2" data-id="element-3033">
                <span className="text-sm text-gray-500 block" data-id="element-3034">
                  Campaign Name
                </span>
                <span className="text-sm font-medium text-gray-900" data-id="element-3035">
                  {campaign.name || <span className="text-gray-400 italic" data-id="element-3036">Not set</span>}
                </span>
              </div>
              <div data-id="element-3037">
                <span className="text-sm text-gray-500 block" data-id="element-3038">Subject</span>
                <span className="text-sm font-medium text-gray-900" data-id="element-3039">
                  {campaign.subject}
                </span>
              </div>
              <div data-id="element-3040">
                <span className="text-sm text-gray-500 block" data-id="element-3041">From</span>
                <span className="text-sm font-medium text-gray-900" data-id="element-3042">
                  {campaign.fromName} &lt;{campaign.fromEmail}&gt;
                </span>
              </div>
              <div data-id="element-3043">
                <span className="text-sm text-gray-500 block" data-id="element-3044">
                  Audience Size
                </span>
                <span className="text-sm font-medium text-gray-900" data-id="element-3045">
                  {audienceResult ? `${audienceResult.count} contacts` : 'Not calculated'}
                </span>
              </div>
              <div data-id="element-3046">
                <span className="text-sm text-gray-500 block" data-id="element-3047">Targeting</span>
                <div className="flex flex-wrap gap-1 mt-1" data-id="element-3048">
                  {campaign.targetRegions.length > 0 && <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800" data-id="element-3049">
                      {campaign.targetRegions.length} Regions
                    </span>}
                  {campaign.targetMailingLists.length > 0 && <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800" data-id="element-3050">
                      {campaign.targetMailingLists.length} Lists
                    </span>}
                  {campaign.targetTags.length > 0 && <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800" data-id="element-3051">
                      {campaign.targetTags.length} Tags
                    </span>}
                  {selectedAudienceIds.length > 0 && <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800" data-id="element-3052">
                      {selectedAudienceIds.length} Saved Audience
                      {selectedAudienceIds.length !== 1 ? 's' : ''}
                    </span>}
                </div>
                {selectedAudienceIds.length > 0 && <div className="mt-2 space-y-1" data-id="element-3053">
                    {selectedAudienceIds.map(id => {
                const aud = savedAudiences.find(a => a.id === id);
                if (!aud) return null;
                return <div key={id} className="text-xs text-gray-500 flex items-center gap-1" data-id="element-3054">
                          <Target className="h-3 w-3 text-amber-500" data-id="element-3055" />
                          {aud.name} — {getAudienceRuleSummary(aud)}
                        </div>;
              })}
                  </div>}
              </div>
            </div>
          </div>

          <div className="border border-gray-200 rounded-lg p-4" data-id="element-3056">
            <h3 className="text-sm font-medium text-gray-900 mb-2" data-id="element-3057">
              Compliance Checklist
            </h3>
            <div className="space-y-2" data-id="element-3058">
              <div className="flex items-center gap-2 text-sm text-green-700" data-id="element-3059">
                <CheckCircle2 className="h-4 w-4" data-id="element-3060" />
                Sending to confirmed subscribers only
              </div>
              <div className="flex items-center gap-2 text-sm text-green-700" data-id="element-3061">
                <CheckCircle2 className="h-4 w-4" data-id="element-3062" />
                Unsubscribe link included automatically
              </div>
            </div>
          </div>
        </div>}

      {/* Step 4: Export */}
      {currentStep === 4 && !exportResult && <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-8 space-y-6" data-id="element-3063">
          <div className="text-center py-4" data-id="element-3064">
            <div className="mx-auto h-16 w-16 bg-purple-100 rounded-full flex items-center justify-center" data-id="element-3065">
              <Database className="h-8 w-8 text-purple-600" data-id="element-3066" />
            </div>
            <h2 className="mt-4 text-2xl font-semibold text-gray-900" data-id="element-3067">
              Push to Zoho CRM
            </h2>
            <p className="mt-2 text-gray-500" data-id="element-3068">
              This will update <strong data-id="element-3069">{audienceResult?.count || 0}</strong>{' '}
              contacts in Zoho CRM with a Bucket ID.
            </p>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3" data-id="element-3070">
            <div className="flex items-center justify-between" data-id="element-3071">
              <span className="text-sm text-blue-700" data-id="element-3072">Bucket ID:</span>
              <code className="text-sm font-mono bg-white px-2 py-1 rounded border border-blue-200 text-blue-900" data-id="element-3073">
                {bucketConfig.bucketId}
              </code>
            </div>
            <div className="flex items-center justify-between" data-id="element-3074">
              <span className="text-sm text-blue-700" data-id="element-3075">Contacts:</span>
              <span className="text-sm font-medium text-blue-900" data-id="element-3076">
                {audienceResult?.count || 0}
              </span>
            </div>
            <div className="flex items-center justify-between" data-id="element-3077">
              <span className="text-sm text-blue-700" data-id="element-3078">Batches needed:</span>
              <span className="text-sm font-medium text-blue-900" data-id="element-3079">
                {Math.ceil((audienceResult?.count || 0) / 100)}
              </span>
            </div>
          </div>

          <div className="space-y-3" data-id="element-3080">
            <label className="block text-sm font-medium text-gray-700" data-id="element-3081">
              Bucket Expiry (Optional)
            </label>
            <div className="grid grid-cols-4 gap-3" data-id="element-3082">
              {[null, 24, 48, 72].map(hours => <button key={hours || 'none'} onClick={() => setBucketConfig(prev => ({
            ...prev,
            expiryHours: hours
          }))} disabled={isExporting} className={`px-4 py-2 text-sm font-medium rounded-md border ${bucketConfig.expiryHours === hours ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'} disabled:opacity-50`} data-id="element-3083">
                  {hours ? `${hours} hours` : 'No expiry'}
                </button>)}
            </div>
          </div>

          {/* Progress Display */}
          {isExporting && exportProgress && <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 space-y-3" data-id="element-3084">
              <div className="flex items-center justify-between text-sm" data-id="element-3085">
                <span className="text-purple-700 font-medium" data-id="element-3086">
                  Updating contacts...
                </span>
                <span className="text-purple-900 font-mono text-xs" data-id="element-3087">
                  Batch {exportProgress.currentBatch} /{' '}
                  {exportProgress.totalBatches}
                </span>
              </div>
              <div className="w-full bg-purple-200 rounded-full h-2.5" data-id="element-3088">
                <div className="bg-purple-600 h-2.5 rounded-full transition-all duration-300" style={{
            width: `${Math.round(exportProgress.processedContacts / exportProgress.totalContacts * 100)}%`
          }} data-id="element-3089" />
              </div>
              <div className="flex justify-between text-xs text-purple-600" data-id="element-3090">
                <span data-id="element-3091">
                  {exportProgress.processedContacts} /{' '}
                  {exportProgress.totalContacts} contacts processed
                </span>
                <span data-id="element-3092">
                  {exportProgress.updatedCount} updated
                  {exportProgress.failedCount > 0 ? `, ${exportProgress.failedCount} failed` : ''}
                </span>
              </div>
            </div>}

          {isExporting && !exportProgress && <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 text-center" data-id="element-3093">
              <Loader2 className="h-5 w-5 animate-spin text-purple-600 mx-auto" data-id="element-3094" />
              <p className="text-sm text-purple-700 mt-2" data-id="element-3095">
                Preparing export...
              </p>
            </div>}

          <div className="flex justify-center gap-4 pt-4" data-id="element-3096">
            <button onClick={() => setCurrentStep(3)} disabled={isExporting} className="px-6 py-3 bg-white border border-gray-300 text-gray-700 font-medium rounded-md hover:bg-gray-50 disabled:opacity-50" data-id="element-3097">
              Back
            </button>
            <button onClick={handleExport} disabled={isExporting || !audienceResult?.count} className="inline-flex items-center gap-2 px-6 py-3 bg-purple-600 text-white font-medium rounded-md hover:bg-purple-700 disabled:opacity-50" data-id="element-3098">
              {isExporting ? <>
                  <Loader2 className="h-5 w-5 animate-spin" data-id="element-3099" />
                  Pushing to CRM...
                </> : <>
                  <ExternalLink className="h-5 w-5" data-id="element-3100" />
                  Push to Zoho CRM
                </>}
            </button>
          </div>
        </div>}

      {/* Export Success */}
      {currentStep === 4 && exportResult?.success && <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-8 space-y-6" data-id="element-3101">
          <div className="text-center" data-id="element-3102">
            <div className="mx-auto h-16 w-16 bg-green-100 rounded-full flex items-center justify-center" data-id="element-3103">
              <CheckCircle2 className="h-8 w-8 text-green-600" data-id="element-3104" />
            </div>
            <h2 className="mt-4 text-2xl font-semibold text-gray-900" data-id="element-3105">
              Export Successful!
            </h2>
            <p className="mt-2 text-gray-600" data-id="element-3106">
              <strong data-id="element-3107">{exportResult.updatedCount}</strong> contacts updated in
              Zoho CRM.
            </p>
          </div>

          {exportResult.failedCount > 0 && <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4" data-id="element-3108">
              <div className="flex items-start gap-2" data-id="element-3109">
                <AlertTriangle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" data-id="element-3110" />
                <div data-id="element-3111">
                  <p className="text-sm font-medium text-yellow-900" data-id="element-3112">
                    {exportResult.failedCount} contact(s) failed to update
                  </p>
                  {exportResult.errors?.length > 0 && <ul className="mt-1 text-xs text-yellow-700 space-y-1" data-id="element-3113">
                      {exportResult.errors.slice(0, 5).map((e: string, i: number) => <li key={i} data-id="element-3114">• {e}</li>)}
                      {exportResult.errors.length > 5 && <li data-id="element-3115">...and {exportResult.errors.length - 5} more</li>}
                    </ul>}
                </div>
              </div>
            </div>}

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6" data-id="element-3116">
            <h3 className="text-base font-semibold text-blue-900 mb-4" data-id="element-3117">
              Next Steps in Zoho Campaigns
            </h3>
            <ol className="space-y-4 text-sm text-blue-900" data-id="element-3118">
              <li className="flex gap-3" data-id="element-3119">
                <span className="font-semibold" data-id="element-3120">1.</span>
                <span data-id="element-3121">Go to Zoho Campaigns and create a Segment</span>
              </li>
              <li className="flex gap-3" data-id="element-3122">
                <span className="font-semibold" data-id="element-3123">2.</span>
                <div className="flex-1" data-id="element-3124">
                  <p className="mb-2" data-id="element-3125">Use this filter criteria:</p>
                  <div className="bg-white border border-blue-300 rounded p-3 font-mono text-sm flex justify-between items-center" data-id="element-3126">
                    <code data-id="element-3127">Bucket_ID equals {bucketConfig.bucketId}</code>
                    <button onClick={() => handleCopyToClipboard(`Bucket_ID equals ${bucketConfig.bucketId}`, 'bucket-filter')} className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded hover:bg-blue-200" data-id="element-3128">
                      {copiedItem === 'bucket-filter' ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                </div>
              </li>
              <li className="flex gap-3" data-id="element-3129">
                <span className="font-semibold" data-id="element-3130">3.</span>
                <span data-id="element-3131">
                  Send your campaign, then clear the bucket from Send Buckets
                  page
                </span>
              </li>
            </ol>
          </div>

          <div className="text-center pt-4" data-id="element-3132">
            <button onClick={() => navigate('/campaigns')} className="px-6 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700" data-id="element-3133">
              Return to Campaigns
            </button>
          </div>
        </div>}

      {/* Export Failed */}
      {currentStep === 4 && exportResult && !exportResult.success && <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-8 space-y-6" data-id="element-3134">
          <div className="text-center" data-id="element-3135">
            <div className="mx-auto h-16 w-16 bg-red-100 rounded-full flex items-center justify-center" data-id="element-3136">
              <X className="h-8 w-8 text-red-600" data-id="element-3137" />
            </div>
            <h2 className="mt-4 text-2xl font-semibold text-gray-900" data-id="element-3138">
              Export Failed
            </h2>
            <p className="mt-2 text-gray-500" data-id="element-3139">{exportResult.error}</p>
          </div>
          <div className="text-center" data-id="element-3140">
            <button onClick={() => {
          setExportResult(null);
          setExportProgress(null);
        }} className="px-6 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700" data-id="element-3141">
              Try Again
            </button>
          </div>
        </div>}

      {/* Navigation Buttons (Steps 1-3) */}
      {currentStep < 4 && <div className="flex justify-between pt-6 border-t border-gray-200" data-id="element-3142">
          <button onClick={() => setCurrentStep(Math.max(1, currentStep - 1))} disabled={currentStep === 1} className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 disabled:opacity-50" data-id="element-3143">
            Back
          </button>
          <button onClick={() => {
        if (currentStep === 1) {
          const hasCrmCriteria = campaign.targetRegions.length > 0 || campaign.targetMailingLists.length > 0 || campaign.targetTags.length > 0;
          const hasAudienceCriteria = selectedAudienceIds.length > 0;
          // If no criteria at all, block
          if (!hasCrmCriteria && !hasAudienceCriteria) {
            toast.error('Please select at least one criteria or saved audience');
            return;
          }
          // If CRM criteria are set but audience hasn't been built, require build
          if (hasCrmCriteria && !audienceResult) {
            toast.error('Please click "Build Audience" to calculate your audience size');
            return;
          }
          // If audience was built but is empty
          if (audienceResult && audienceResult.count === 0) {
            toast.error('Audience is empty — adjust your criteria');
            return;
          }
          // If only saved audiences selected (no CRM criteria), allow proceeding
          // The saved audience rules will be applied during send
        }
        setCurrentStep(Math.min(4, currentStep + 1));
      }} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700" data-id="element-3144">
            Next Step
            <ArrowRight className="h-4 w-4" data-id="element-3145" />
          </button>
        </div>}
    </div>;
}